<script setup lang="ts" generic="T extends Record<string, any>">
import { ref, type VNode } from "vue";
import IconManager from "@/core/IconManager.vue";
import Pagination from "@/core/components/table/Pagination.vue";
import PageSizeSelect from "@/core/components/table/PageSizeSelect.vue";
import { Option } from "@/core/components/table/PageSizeSelect.vue";

interface TableColumn<T = any> {
  key: string;
  title: string;
  sortable?: boolean;
  render?: (value: any, row: T) => VNode | string;
}

interface TablePagination {
  goToPageCallBack: (page: number, pageSize: number) => Promise<void>;
  pageSize?: number;
}

interface TablePageSize {
  changeCallBack: (pageSize: number, currentPage: number) => Promise<void>;
  initialPageSize?: Option;
  options?: Option[];
}

type SortDirection = "asc" | "desc" | null;

const props = defineProps<{
  columns: TableColumn[];
  data: T[];
  totalEntries: number;
  isLoading?: boolean;
  pagination?: TablePagination;
  pageSizeOptions?: TablePageSize;
  sortCallBack?: (key: string, orderDirection: string) => Promise<boolean>;
}>();

const sortState = ref<{
  key: string | null;
  direction: SortDirection;
}>({ key: null, direction: null });

const currentPage = ref<number>(1);
const currentPageSize = ref<number>(props.pagination?.pageSize ?? 10);

function getCellContent(column: TableColumn, row: any) {
  const value = row[column.key];
  if (column.render) {
    return column.render(value, row);
  }
  return value;
}

async function handlePageSizeChange(pageSize: number) {
  currentPageSize.value = pageSize;
  props.pageSizeOptions?.changeCallBack(pageSize, currentPage.value);
}

async function toggleSort(col: TableColumn) {
  let nextDirection: SortDirection = "asc";

  if (sortState.value.key === col.key) {
    if (sortState.value.direction === "asc") nextDirection = "desc";
    else if (sortState.value.direction === "desc") nextDirection = null;
    else nextDirection = "asc";
  }
  const response = await props.sortCallBack!(col.key, nextDirection ?? "");
  if (!response) return;

  sortState.value = {
    key: nextDirection ? col.key : null,
    direction: nextDirection,
  };
}
</script>

<template>
  <div class="flex flex-col gap-4 w-full bg-white rounded-xl shadow-md p-4">
    <PageSizeSelect
      v-if="pageSizeOptions"
      @change="handlePageSizeChange"
      :initialPageSize="pageSizeOptions.initialPageSize"
      :options="pageSizeOptions.options"
    />

    <!-- wrapper needed so the overlay can position itself over just the table -->
    <div class="relative">
      <table
        class="w-full bg-white"
        :class="{ 'pointer-events-none select-none': isLoading }"
      >
        <thead>
          <tr class="bg-background">
            <th
              class="text-left px-3 py-2.5"
              v-for="col in columns"
              :key="col.key"
            >
              <div class="flex flex-row items-center gap-2">
                <span class="text-sm text-light-gray">
                  {{ col.title }}
                </span>
                <button v-if="col.sortable" @click="toggleSort(col)">
                  <IconManager
                    :class="{ 'text-primary': sortState.key == col.key }"
                    :name="
                      sortState.key != col.key || sortState.direction == null
                        ? 'SortNeutral'
                        : sortState.direction == 'asc'
                          ? 'SortAsc'
                          : 'SortDes'
                    "
                  />
                </button>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in data" :key="row.id">
            <td
              class="border-b border-[#EEF2F6] px-3 py-2.5"
              v-for="col in columns"
              :key="col.key"
            >
              <component
                :is="() => getCellContent(col, row)"
                v-if="col.render"
              />
              <template v-else>{{ getCellContent(col, row) }}</template>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- loading overlay -->
      <div
        v-if="isLoading"
        class="absolute inset-0 flex items-center justify-center bg-white/60 z-10"
      >
        <IconManager name="Gear" class="text-primary h-8 w-8 animate-spin" />
      </div>
    </div>

    <Pagination
      v-if="pagination"
      v-model:currentPage="currentPage"
      :totalEntries="totalEntries"
      :goToPage="pagination.goToPageCallBack"
      :pageSize="currentPageSize"
    />
  </div>
</template>
